import React, { useState, useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { apiClient } from "~/api/client"
import AuthenticatedLayout from "~/components/AuthenticatedLayout"
import Markdown from "~/components/Markdown"
import type { Chat, ChatDetail, ChatMessage } from "~/types"

const ChatView: React.FC = () => {
  const [chats, setChats] = useState<Chat[]>([])
  const [currentChat, setCurrentChat] = useState<ChatDetail | null>(null)
  const [expandedSources, setExpandedSources] = useState<Set<string>>(new Set())
  const [messageInput, setMessageInput] = useState("")
  const [loading, setLoading] = useState(false)
  const [sending, setSending] = useState(false)
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null)
  const messagesEndRef = useRef<React.ElementRef<"div">>(null)
  const textareaRef = useRef<React.ElementRef<"textarea">>(null)
  const navigate = useNavigate()

  useEffect(() => {
    loadChats()
  }, [])

  useEffect(() => {
    scrollToBottom()
  }, [currentChat?.messages])

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }

  const loadChats = async () => {
    try {
      const data = await apiClient.getChats()
      setChats(data)
    } catch {
      // Failed to load chats
    }
  }

  const loadChat = async (chatId: string) => {
    setLoading(true)
    try {
      const data = await apiClient.getChat(chatId)
      setCurrentChat(data)
    } catch {
      // Failed to load chat
    } finally {
      setLoading(false)
    }
  }

  const createNewChat = async () => {
    try {
      const data = await apiClient.createChat()
      setChats([data, ...chats])
      setCurrentChat(data)
    } catch {
      // Failed to create chat
    }
  }

  const deleteChat = async (chatId: string) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Delete this chat?")) {
      return
    }

    try {
      await apiClient.deleteChat(chatId)
      setChats(chats.filter((c) => c.id !== chatId))
      if (currentChat?.id === chatId) {
        setCurrentChat(null)
      }
    } catch {
      // Failed to delete chat
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessageInput(e.target.value)
    e.target.style.height = "auto"
    e.target.style.height = `${e.target.scrollHeight}px`
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      e.currentTarget.form?.requestSubmit()
    }
  }

  const sendMessage = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!messageInput.trim() || !currentChat || sending) {
      return
    }

    const content = messageInput.trim()
    setMessageInput("")
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto"
    }
    setSending(true)

    // Optimistically add user message
    const userMessage: ChatMessage = {
      id: "temp-" + Date.now(),
      role: "user",
      content,
      model: null,
      created_at: new Date().toISOString(),
    }

    setCurrentChat({
      ...currentChat,
      messages: [...currentChat.messages, userMessage],
    })

    try {
      const response = await apiClient.sendChatMessage(currentChat.id, content)

      // Attach sources to the assistant message
      const assistantMessage: ChatMessage = {
        ...response.message,
        sources: response.sources,
      }

      // Just append the messages - no need to refetch everything
      setCurrentChat({
        ...currentChat,
        title: response.title ?? currentChat.title,
        messages: [
          ...currentChat.messages.filter((m) => m.id !== userMessage.id),
          userMessage,
          assistantMessage,
        ],
        message_count: currentChat.message_count + 2,
        updated_at: response.message.created_at,
      })

      // Update just the relevant chat in the list without refetching all
      setChats(
        chats.map((c) =>
          c.id === currentChat.id
            ? {
                ...c,
                title: response.title ?? c.title,
                message_count: c.message_count + 2,
                updated_at: response.message.created_at,
              }
            : c,
        ),
      )
    } catch {
      // Failed to send message - remove optimistic message on error
      setCurrentChat({
        ...currentChat,
        messages: currentChat.messages.filter((m) => m.id !== userMessage.id),
      })
    } finally {
      setSending(false)
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    })
  }

  const formatTime = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    })
  }

  const copyToClipboard = async (content: string, messageId: string) => {
    try {
      await navigator.clipboard.writeText(content)
      setCopiedMessageId(messageId)
      setTimeout(() => setCopiedMessageId(null), 2000)
    } catch {
      // Failed to copy to clipboard
    }
  }

  return (
    <AuthenticatedLayout>
      <div
        style={{
          display: "flex",
          height: "calc(100vh - 81px)",
          overflow: "hidden",
        }}
      >
        {/* Sidebar */}
        <div
          style={{
            width: "300px",
            backgroundColor: "#f8f9fa",
            borderRight: "1px solid #ccc",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div style={{ padding: "16px", borderBottom: "1px solid #ccc" }}>
            <button
              type="button"
              onClick={createNewChat}
              style={{
                width: "100%",
                padding: "8px",
                backgroundColor: "#007bff",
                color: "white",
                border: "none",
                borderRadius: "8px",
                fontSize: "14px",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              + New Chat
            </button>
          </div>

          {/* Chat List */}
          <div style={{ flex: 1, overflowY: "auto", padding: "12px" }}>
            {chats.map((chat) => (
              <div
                key={chat.id}
                onClick={() => loadChat(chat.id)}
                style={{
                  padding: "12px",
                  marginBottom: "8px",
                  backgroundColor:
                    currentChat?.id === chat.id ? "#e3f2fd" : "white",
                  border: "1px solid #ccc",
                  borderRadius: "8px",
                  cursor: "pointer",
                  position: "relative",
                }}
              >
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: 600,
                    marginBottom: "4px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    paddingRight: "60px",
                  }}
                >
                  {chat.title}
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#666",
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <span>{formatDate(chat.updated_at)}</span>
                  <span>{chat.message_count} msgs</span>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    deleteChat(chat.id)
                  }}
                  style={{
                    position: "absolute",
                    top: "8px",
                    right: "8px",
                    padding: "2px 6px",
                    backgroundColor: "#dc3545",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    fontSize: "10px",
                    cursor: "pointer",
                  }}
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Main Chat Area */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
          {loading ? (
            <div
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              Loading...
            </div>
          ) : !currentChat ? (
            <div
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#666",
              }}
            >
              Select a chat or create a new one
            </div>
          ) : (
            <>
              {/* Messages */}
              <div
                style={{
                  flex: 1,
                  overflowY: "auto",
                  padding: "24px",
                  backgroundColor: "#f5f5f5",
                }}
              >
                {currentChat.messages.map((message) => {
                  const sources = message.sources
                  return (
                    <div
                      key={message.id}
                      style={{
                        marginBottom: "16px",
                        display: "flex",
                        justifyContent:
                          message.role === "user" ? "flex-end" : "flex-start",
                      }}
                    >
                      <div style={{ maxWidth: "70%", position: "relative" }}>
                        <div
                          style={{
                            padding: "12px 16px",
                            borderRadius: "8px",
                            backgroundColor:
                              message.role === "user" ? "#007bff" : "white",
                            color: message.role === "user" ? "white" : "#333",
                            border:
                              message.role === "user"
                                ? "none"
                                : "1px solid #ccc",
                            position: "relative",
                          }}
                        >
                          <button
                            type="button"
                            onClick={() =>
                              copyToClipboard(message.content, message.id)
                            }
                            style={{
                              position: "absolute",
                              top: "8px",
                              right: "8px",
                              padding: "4px 8px",
                              backgroundColor:
                                message.role === "user"
                                  ? "rgba(255, 255, 255, 0.2)"
                                  : "#f8f9fa",
                              color: message.role === "user" ? "white" : "#666",
                              border: "none",
                              borderRadius: "4px",
                              fontSize: "11px",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                            title="Copy message"
                          >
                            {copiedMessageId === message.id ? "✓" : "📋"}
                          </button>
                          <div
                            style={{ fontSize: "14px", paddingRight: "40px" }}
                          >
                            {message.role === "user" ? (
                              <div style={{ whiteSpace: "pre-wrap" }}>
                                {message.content}
                              </div>
                            ) : (
                              <Markdown>{message.content}</Markdown>
                            )}
                          </div>
                          <div
                            style={{
                              fontSize: "11px",
                              marginTop: "4px",
                              opacity: 0.7,
                            }}
                          >
                            {formatTime(message.created_at)}
                          </div>
                        </div>
                        {sources && sources.length > 0 && (
                          <div
                            style={{
                              marginTop: "8px",
                              backgroundColor: "#f8f9fa",
                              border: "1px solid #dee2e6",
                              borderRadius: "8px",
                              fontSize: "12px",
                            }}
                          >
                            <div
                              onClick={() => {
                                const newExpanded = new Set(expandedSources)
                                if (newExpanded.has(message.id)) {
                                  newExpanded.delete(message.id)
                                } else {
                                  newExpanded.add(message.id)
                                }
                                setExpandedSources(newExpanded)
                              }}
                              style={{
                                padding: "8px",
                                fontWeight: 600,
                                color: "#666",
                                cursor: "pointer",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <span>
                                {sources.length} knowledge{" "}
                                {sources.length === 1 ? "entry" : "entries"}{" "}
                                used
                              </span>
                              <span style={{ fontSize: "14px" }}>
                                {expandedSources.has(message.id) ? "▼" : "▶"}
                              </span>
                            </div>
                            {expandedSources.has(message.id) && (
                              <div style={{ padding: "0 8px 8px 8px" }}>
                                {sources.map((source, idx) => (
                                  <div
                                    key={source.id}
                                    onClick={() =>
                                      navigate(`/entries/${source.id}`)
                                    }
                                    style={{
                                      padding: "8px",
                                      marginTop: idx === 0 ? "4px" : "8px",
                                      backgroundColor: "white",
                                      border: "1px solid #ccc",
                                      borderRadius: "4px",
                                      cursor: "pointer",
                                    }}
                                    onMouseEnter={(e) =>
                                      (e.currentTarget.style.backgroundColor =
                                        "#e3f2fd")
                                    }
                                    onMouseLeave={(e) =>
                                      (e.currentTarget.style.backgroundColor =
                                        "white")
                                    }
                                  >
                                    <div
                                      style={{
                                        fontWeight: 600,
                                        color: "#007bff",
                                        marginBottom: "4px",
                                      }}
                                    >
                                      {source.title || "Untitled"}
                                    </div>
                                    <div
                                      style={{
                                        color: "#666",
                                        fontSize: "11px",
                                      }}
                                    >
                                      #{source.channel_name}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })}
                {sending && (
                  <div
                    style={{
                      marginBottom: "16px",
                      display: "flex",
                      justifyContent: "flex-start",
                    }}
                  >
                    <div
                      style={{
                        padding: "12px 16px",
                        borderRadius: "8px",
                        backgroundColor: "white",
                        border: "1px solid #ccc",
                        color: "#666",
                      }}
                    >
                      Thinking...
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <form
                onSubmit={sendMessage}
                style={{
                  padding: "16px",
                  backgroundColor: "white",
                  borderTop: "1px solid #ccc",
                }}
              >
                <div style={{ display: "flex", gap: "8px", alignItems: "flex-end" }}>
                  <textarea
                    ref={textareaRef}
                    value={messageInput}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    placeholder="Ask a question..."
                    disabled={sending}
                    rows={1}
                    style={{
                      flex: 1,
                      padding: "12px",
                      borderRadius: "8px",
                      border: "1px solid #ccc",
                      fontSize: "14px",
                      resize: "none",
                      overflow: "hidden",
                      lineHeight: "1.5",
                    }}
                  />
                  <button
                    type="submit"
                    disabled={sending || !messageInput.trim()}
                    style={{
                      padding: "12px 24px",
                      backgroundColor:
                        sending || !messageInput.trim() ? "#6c757d" : "#007bff",
                      color: "white",
                      border: "none",
                      borderRadius: "8px",
                      fontSize: "14px",
                      fontWeight: 600,
                      cursor:
                        sending || !messageInput.trim()
                          ? "not-allowed"
                          : "pointer",
                    }}
                  >
                    Send
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </AuthenticatedLayout>
  )
}

export default ChatView
